/* HPW Wishlist: native Shopify storefront controls over a signed app proxy. */
(function () {
  "use strict";

  var API = "/apps/nexus-wishlist";
  var PAGE_PATH = "/pages/wishlist";
  var DEFAULT_LIST = "Wishlist";
  var VISUAL_REVALIDATE_MS = 60000;
  var visualRequestGeneration = 0;
  var visual = {
    labels: {},
    sections: {},
    colors: {},
    controls: {},
    density: "comfortable",
    radius: "none",
    typography: "modern"
  };

  function one(selector, root) { return (root || document).querySelector(selector); }
  function all(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }
  function text(value) {
    var node = document.createElement("div");
    node.textContent = value == null ? "" : String(value);
    return node.innerHTML;
  }
  function sameOriginUrl(value, fallback) {
    if (!value) return fallback || "";
    try {
      var url = new URL(String(value), window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search + url.hash : (fallback || "");
    } catch (_) { return fallback || ""; }
  }
  function mediaUrl(value) {
    if (!value) return "";
    try {
      var url = new URL(String(value), window.location.origin);
      return url.protocol === "https:" &&
        (url.hostname === "cdn.shopify.com" || url.hostname.endsWith(".shopifycdn.com")) ?
        url.href : "";
    } catch (_) { return ""; }
  }
  function trustedShareUrl(value) {
    try {
      var url = new URL(String(value), window.location.origin);
      return url.protocol === "https:" &&
        (url.hostname === "hpwcards.com" || url.hostname === "www.hpwcards.com") ?
        url.href : "";
    } catch (_) { return ""; }
  }
  function css(value) {
    return window.CSS && window.CSS.escape ? window.CSS.escape(String(value)) :
      String(value).replace(/["\\]/g, "\\$&");
  }
  function id(value) {
    if (value == null) return "";
    var match = String(value).match(/(\d+)(?:\D*)$/);
    return match ? match[1] : "";
  }
  function cad(value, cents) {
    var amount = cents != null ? Number(cents) / 100 : Number(value || 0);
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(amount);
  }
  function object(value) { return value && typeof value === "object" && !Array.isArray(value) ? value : {}; }
  function choice(value, allowed, fallback) {
    return typeof value === "string" && allowed.indexOf(value) !== -1 ? value : fallback;
  }
  function selectedChoice(value, allowed) {
    return typeof value === "string" && allowed.indexOf(value) !== -1 ? value : undefined;
  }
  function selectedSetting(objects, keys, allowed) {
    for (var i = 0; i < objects.length; i += 1) {
      for (var j = 0; j < keys.length; j += 1) {
        var value = selectedChoice(objects[i][keys[j]], allowed);
        if (value) return value;
      }
    }
    return undefined;
  }
  function setting(objects, keys) {
    for (var i = 0; i < objects.length; i += 1) {
      for (var j = 0; j < keys.length; j += 1) {
        if (objects[i][keys[j]] != null) return objects[i][keys[j]];
      }
    }
    return undefined;
  }
  function label(key, fallback) {
    var value = visual.labels[key];
    return typeof value === "string" && value.trim() ? value.trim().slice(0, 160) : fallback;
  }
  function validColor(value) { return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value); }
  function luminance(value) {
    if (!validColor(value)) return 0;
    var channels = [value.slice(1, 3), value.slice(3, 5), value.slice(5, 7)].map(function (pair) {
      var channel = parseInt(pair, 16) / 255;
      return channel <= .04045 ? channel / 12.92 : Math.pow((channel + .055) / 1.055, 2.4);
    });
    return .2126 * channels[0] + .7152 * channels[1] + .0722 * channels[2];
  }
  function contrast(first, second) {
    var a = luminance(first);
    var b = luminance(second);
    return (Math.max(a, b) + .05) / (Math.min(a, b) + .05);
  }
  function applyVisualSettings(payload) {
    var root = object(object(payload).settings || payload);
    var storefront = object(root.storefront || root.public);
    var modules = object(root.modules || root.moduleOverrides);
    var wishlist = object(modules.wishlist || root.wishlist);
    var darkMode = document.documentElement.classList.contains("dark") ||
      document.body.classList.contains("dark") ||
      (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
    // Current public projections carry both palettes. Never blend light values
    // into dark mode; legacy `colors` remains the light-compatible shape.
    var colors = object(darkMode ? storefront.dark : (storefront.light || storefront.colors));
    var foundation = object(storefront.foundation);
    var presentation = object(wishlist.presentation);
    var layout = object(wishlist.layout);
    var cards = object(wishlist.cards || wishlist.itemCards);
    var images = object(wishlist.images);
    var buttons = object(wishlist.buttons);
    var dialogs = object(wishlist.dialogs);
    var banners = object(wishlist.banners);
    var roots = all("[data-hpw-wishlist-root]");
    var managedProperties = [
      "--hpw-wish-bg", "--hpw-wish-surface", "--hpw-wish-ink", "--hpw-wish-muted",
      "--hpw-wish-line", "--hpw-wish-green", "--hpw-wish-primary-text",
      "--hpw-wish-success", "--hpw-wish-warning", "--hpw-wish-danger",
      "--hpw-wish-link", "--hpw-wish-focus", "--hpw-wish-overlay",
      "--hpw-wish-radius", "--hpw-wish-density", "--hpw-wish-font",
      "--hpw-wish-page-width"
    ];
    var managedAttributes = [
      "data-hpw-wish-content-width", "data-hpw-wish-grid", "data-hpw-wish-card",
      "data-hpw-wish-image", "data-hpw-wish-button", "data-hpw-wish-dialog",
      "data-hpw-wish-banner", "data-hpw-wish-toast", "data-hpw-wish-spacing",
      "data-hpw-wish-header", "data-hpw-wish-heading", "data-hpw-wish-form",
       "data-hpw-wish-table", "data-hpw-wish-shadow", "data-hpw-wish-border", "data-hpw-wish-focus",
      "data-hpw-wish-loading", "data-hpw-wish-empty", "data-hpw-wish-responsive",
      "data-hpw-wish-motion"
    ];
    roots.forEach(function (root) {
      managedProperties.forEach(function (name) { root.style.removeProperty(name); });
      managedAttributes.forEach(function (name) { root.removeAttribute(name); });
    });
    visual.labels = Object.assign({}, object(wishlist.copy), object(wishlist.labels));
    visual.payload = payload;
    visual.sections = object(wishlist.optionalSections || wishlist.visibility);
    visual.density = choice(storefront.density, ["compact", "comfortable", "spacious"], "comfortable");
    visual.radius = choice(storefront.cornerRadius || storefront.radius, ["none", "small", "medium", "large", "square", "soft", "rounded"], "none");
    visual.typography = choice(storefront.typography || storefront.font, ["system", "modern", "classic", "inter", "humanist"], "modern");
    visual.colors = colors;
    visual.controls = {
      pageWidth: foundation.contentWidth === "full" ? "full" :
        (selectedSetting([presentation, layout, wishlist, foundation], ["pageWidth", "contentWidth"], ["narrow", "standard", "wide"]) || "wide"),
      grid: selectedSetting([presentation, layout, wishlist], ["itemGrid", "grid"], ["list", "compact-grid", "roomy-grid"]) || "roomy-grid",
      card: selectedSetting([presentation, cards, wishlist, foundation], ["cardTreatment", "style", "cardStyle", "card"], ["bordered", "elevated", "flat"]) || "bordered",
      image: selectedSetting([presentation, images, wishlist, foundation], ["imageTreatment", "fit", "imageFit", "image"], ["contain", "cover", "natural", "square", "rounded"]) || "contain",
      button: selectedSetting([presentation, buttons, wishlist, foundation], ["buttonTreatment", "style", "buttonStyle", "button"], ["outline", "solid", "soft"]) || "solid",
      dialog: selectedSetting([presentation, dialogs, wishlist, foundation], ["dialogSize", "style", "dialogStyle", "dialog"], ["standard", "compact", "wide"]) || "standard",
      banner: selectedSetting([presentation, banners, wishlist, foundation], ["bannerTreatment", "style", "bannerStyle", "banner"], ["solid", "subtle", "outlined"]) || "subtle",
      toast: selectedSetting([presentation, wishlist, layout], ["toastPlacement"], ["bottom-center", "bottom-right", "top-right"]) || "bottom-right"
    };
    // Validation must use a palette appropriate to the active mode.  These
    // values are only contrast references; missing public values are never
    // materialized as inline light-mode tokens.
    var background = validColor(colors.background) ? colors.background : (darkMode ? "#101820" : "#f8faf7");
    var surface = validColor(colors.surface) ? colors.surface : (darkMode ? "#17212b" : "#ffffff");
    var colorMap = {
      background: "--hpw-wish-bg", surface: "--hpw-wish-surface", text: "--hpw-wish-ink",
      muted: "--hpw-wish-muted", border: "--hpw-wish-line", primary: "--hpw-wish-green",
      primaryText: "--hpw-wish-primary-text", success: "--hpw-wish-success",
      warning: "--hpw-wish-warning", danger: "--hpw-wish-danger",
      link: "--hpw-wish-link", focus: "--hpw-wish-focus", overlay: "--hpw-wish-overlay"
    };
    Object.keys(colorMap).forEach(function (key) {
      if (!validColor(colors[key])) return;
      var candidate = colors[key];
      var minimum = { text: 4.5, muted: 4.5, primary: 3, success: 3, warning: 3, danger: 3, link: 4.5, focus: 3 }[key];
      // Do not replace an unsafe dark palette value with a light default.
      // Omission leaves the component's native token intact.
      if (minimum && (contrast(candidate, background) < minimum || contrast(candidate, surface) < minimum)) return;
       roots.forEach(function (root) { root.style.setProperty(colorMap[key], candidate); });
    });
    if (wishlist.inherit === false && validColor(wishlist.accent) &&
        contrast(wishlist.accent, background) >= 3 && contrast(wishlist.accent, surface) >= 3) {
       roots.forEach(function (root) { root.style.setProperty("--hpw-wish-green", wishlist.accent); });
    }
    if (selectedChoice(storefront.cornerRadius || storefront.radius, ["none", "small", "medium", "large", "square", "soft", "rounded"])) {
       roots.forEach(function (root) { root.style.setProperty("--hpw-wish-radius", { none: "0px", square: "0px", small: "4px", soft: "8px", medium: "8px", large: "16px", rounded: "16px" }[visual.radius] || "0px"); });
    }
    if (storefront.density != null) {
       roots.forEach(function (root) { root.style.setProperty("--hpw-wish-density", { compact: ".82", comfortable: "1", spacious: "1.2" }[visual.density] || "1"); });
    }
    if (selectedChoice(storefront.typography || storefront.font, ["system", "modern", "classic", "inter", "humanist"])) {
       roots.forEach(function (root) { root.style.setProperty("--hpw-wish-font", visual.typography === "classic" || visual.typography === "humanist" ? 'Georgia, "Times New Roman", serif' : visual.typography === "system" ? "system-ui, sans-serif" : '"Roboto", sans-serif'); });
    }
    var pageWidthSetting = foundation.contentWidth === "full" ? "full" :
      selectedSetting([presentation, layout, wishlist], ["pageWidth", "contentWidth"], ["narrow", "standard", "wide"]) ||
      selectedChoice(foundation.contentWidth, ["narrow", "standard", "wide"]);
    if (pageWidthSetting) {
       roots.forEach(function (root) {
         root.style.setProperty("--hpw-wish-page-width", { narrow: "760px", standard: "980px", wide: "1180px", full: "none" }[pageWidthSetting]);
         root.setAttribute("data-hpw-wish-content-width", pageWidthSetting);
       });
    }
    var attributeSettings = [
      ["data-hpw-wish-grid", selectedSetting([presentation, layout, wishlist], ["itemGrid", "grid"], ["list", "compact-grid", "roomy-grid"]), ["list", "compact-grid", "roomy-grid"]],
      ["data-hpw-wish-card", selectedSetting([presentation, cards, wishlist, foundation], ["cardTreatment", "style", "cardStyle", "card"], ["bordered", "elevated", "flat"]), ["bordered", "elevated", "flat"]],
      ["data-hpw-wish-image", selectedSetting([presentation, images, wishlist, foundation], ["imageTreatment", "fit", "imageFit", "image"], ["contain", "cover", "natural", "square", "rounded"]), ["contain", "cover", "natural", "square", "rounded"]],
      ["data-hpw-wish-button", selectedSetting([presentation, buttons, wishlist, foundation], ["buttonTreatment", "style", "buttonStyle", "button"], ["outline", "solid", "soft"]), ["outline", "solid", "soft"]],
      ["data-hpw-wish-dialog", selectedSetting([presentation, dialogs, wishlist, foundation], ["dialogSize", "style", "dialogStyle", "dialog"], ["standard", "compact", "wide"]), ["standard", "compact", "wide"]],
      ["data-hpw-wish-banner", selectedSetting([presentation, banners, wishlist, foundation], ["bannerTreatment", "style", "bannerStyle", "banner"], ["solid", "subtle", "outlined"]), ["solid", "subtle", "outlined"]],
      ["data-hpw-wish-toast", selectedSetting([presentation, wishlist, layout], ["toastPlacement"], ["bottom-center", "bottom-right", "top-right"]), ["bottom-center", "bottom-right", "top-right"]],
      ["data-hpw-wish-spacing", foundation.spacing, ["compact", "balanced", "generous"]],
      ["data-hpw-wish-header", foundation.header, ["minimal", "solid", "elevated"]],
      ["data-hpw-wish-heading", foundation.headingScale, ["compact", "balanced", "prominent"]],
      ["data-hpw-wish-form", foundation.form, ["filled", "outlined"]],
       ["data-hpw-wish-table", foundation.table, ["plain", "striped", "separated"]],
      ["data-hpw-wish-shadow", foundation.shadow, ["none", "subtle", "medium"]],
      ["data-hpw-wish-border", foundation.border, ["none", "subtle", "strong"]],
      ["data-hpw-wish-focus", foundation.focus, ["ring", "high-contrast"]],
      ["data-hpw-wish-loading", foundation.loading, ["spinner", "skeleton"]],
      ["data-hpw-wish-empty", foundation.emptyState, ["plain", "card"]],
      ["data-hpw-wish-responsive", foundation.responsiveDensity, ["same", "compact-mobile"]],
      ["data-hpw-wish-motion", storefront.motion, ["none", "reduced", "standard"]]
    ];
    attributeSettings.forEach(function (entry) {
      var selected = selectedChoice(entry[1], entry[2]);
       if (selected) roots.forEach(function (root) { root.setAttribute(entry[0], selected); });
    });
    var banner = object(wishlist.banner || storefront.banner);
    var existing = one(".hpw-wishlist-banner");
    var createdBanner = false;
    if (banner.enabled === true && typeof banner.text === "string" && banner.text.trim()) {
      if (!existing) {
        existing = document.createElement("aside");
         existing.className = "hpw-wishlist-banner";
         existing.dataset.hpwWishlistRoot = "true";
        existing.setAttribute("role", "status");
        document.body.insertBefore(existing, document.body.firstChild);
        createdBanner = true;
      }
      existing.dataset.tone = choice(banner.tone, ["info", "success", "warning"], "info");
       existing.textContent = banner.text.trim().slice(0, 240);
    } else if (existing) existing.remove();
    // The banner is an overlay root appended after this pass collected roots.
    // Restyle it immediately; the second pass sees the existing banner and
    // therefore cannot recurse.
    if (createdBanner) applyVisualSettings(visual.payload);
  }
  function loadVisualSettings() {
    var generation = ++visualRequestGeneration;
    return request("/visual-settings", { cache: "no-store", headers: { "Cache-Control": "no-cache" } }, true).then(function (payload) {
      // Empty is a real publication. Ignore any response older than a request
      // already issued, even when the browser/proxy cannot cancel it.
      if (generation === visualRequestGeneration) applyVisualSettings(payload);
    }).catch(function () {
      /* Published appearance is optional. Functional controls keep safe defaults. */
    });
  }
  function announce(message) {
    var live = one("#hpw-wishlist-status");
    if (!live) {
      live = document.createElement("div");
      live.id = "hpw-wishlist-status";
      live.className = "hpw-wishlist-sr-only";
      live.dataset.hpwWishlistRoot = "true";
      live.setAttribute("role", "status");
      live.setAttribute("aria-live", "polite");
      document.body.appendChild(live);
    }
    live.textContent = "";
    window.setTimeout(function () { live.textContent = message; }, 10);
  }
  function toast(message) {
    announce(message);
    var old = one(".hpw-wishlist-notice");
    if (old) old.remove();
    var node = document.createElement("div");
    node.className = "hpw-wishlist-notice";
    node.dataset.hpwWishlistRoot = "true";
    node.setAttribute("role", "status");
    node.textContent = message;
    document.body.appendChild(node);
    // Toast placement and palette must be present on first paint, not after
    // the next public-settings revalidation.
    if (visual.payload) applyVisualSettings(visual.payload);
    window.setTimeout(function () { node.remove(); }, 3500);
  }
  function request(path, options, isPublic) {
    options = options || {};
    options.credentials = "same-origin";
    options.headers = Object.assign({ Accept: "application/json" }, options.headers || {});
    if (options.body) options.headers["Content-Type"] = "application/json";
    return fetch(API + path, options).then(function (response) {
      if (!isPublic && (response.status === 401 || response.status === 403)) {
        window.location.assign("/account/login?return_url=" + encodeURIComponent(PAGE_PATH));
        return new Promise(function () {});
      }
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          throw new Error(body.message || body.error || "Wishlist request failed");
        });
      }
      return response.status === 204 ? null : response.json();
    });
  }
  function parseJsonScript(root) {
    var scripts = all('script[type="application/json"]', root);
    for (var i = 0; i < scripts.length; i += 1) {
      try {
        var parsed = JSON.parse(scripts[i].textContent || "");
        if (parsed && parsed.product) parsed = parsed.product;
        if (parsed && (parsed.id || parsed.product_id) && parsed.variants) return parsed;
      } catch (_) { /* Other theme JSON is unrelated. */ }
    }
    return null;
  }
  function productIdentity(form) {
    var metaProduct = window.meta && window.meta.product;
    var json = parseJsonScript(form.closest("section") || document) || parseJsonScript(document);
    var variantField = one('[name="id"]', form);
    var variantId = id(variantField && variantField.value);
    var variants = (json && json.variants) || (metaProduct && metaProduct.variants) || [];
    var selected = variants.filter(function (variant) { return id(variant.id) === variantId; })[0];
    var canonical = one('link[rel="canonical"]');
    var handleMatch = canonical && canonical.href.match(/\/products\/([^/?#]+)/);
    var image = one('meta[property="og:image"]');
    return {
      productId: id((json && (json.id || json.product_id)) || (metaProduct && metaProduct.id)),
      variantId: variantId,
      handle: handleMatch ? decodeURIComponent(handleMatch[1]) : "",
      imageUrl: mediaUrl((selected && (selected.featured_image && (selected.featured_image.src || selected.featured_image))) || (image && image.content)),
    };
  }
  function save(button, payload, destination) {
    button.disabled = true;
    var target = destination && destination.id ?
      { listId: String(destination.id) } : { listName: DEFAULT_LIST };
    return request("/items", { method: "POST", body: JSON.stringify(Object.assign(target, payload)) })
      .then(function () {
        button.disabled = false;
        button.setAttribute("aria-pressed", "true");
        var listName = destination && destination.name ? String(destination.name) : DEFAULT_LIST;
        button.textContent = button.classList.contains("hpw-wishlist-heart") ? "♥" : (payload.savedForLater ? "Saved for Later" : "Saved to " + listName);
        toast(payload.savedForLater ? "Saved for later" : "Saved to " + listName);
        return true;
      })
      .catch(function (error) { button.disabled = false; toast(error.message); return false; });
  }

  function chooseList(lists) {
    return new Promise(function (resolve) {
      var dialog = document.createElement("dialog");
      dialog.className = "hpw-wishlist-picker";
       dialog.dataset.hpwWishlistRoot = "true";
      dialog.setAttribute("aria-labelledby", "hpw-wishlist-picker-title");
      var panel = document.createElement("div");
      panel.className = "hpw-wishlist-picker-panel";
      var heading = document.createElement("h2");
      heading.id = "hpw-wishlist-picker-title";
      heading.textContent = label("pickerTitle", "Choose a wishlist");
      var description = document.createElement("p");
      description.textContent = label("pickerDescription", "Where would you like to save this card?");
      var choices = document.createElement("div");
      choices.className = "hpw-wishlist-picker-choices";
      var settled = false;
      function finish(list) {
        if (settled) return;
        settled = true;
        dialog.close();
        dialog.remove();
        resolve(list || null);
      }
      lists.forEach(function (list) {
        var choice = document.createElement("button");
        choice.type = "button";
        choice.className = "hpw-wishlist-button hpw-wishlist-picker-choice";
        choice.textContent = String(list.name || DEFAULT_LIST);
        choice.addEventListener("click", function () { finish(list); });
        choices.appendChild(choice);
      });
      var cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "hpw-wishlist-button hpw-wishlist-picker-cancel";
      cancel.textContent = label("pickerCancel", "Cancel");
      cancel.addEventListener("click", function () { finish(null); });
      dialog.addEventListener("cancel", function (event) {
        event.preventDefault();
        finish(null);
      });
      panel.appendChild(heading);
      panel.appendChild(description);
      panel.appendChild(choices);
      panel.appendChild(cancel);
      dialog.appendChild(panel);
      document.body.appendChild(dialog);
       applyVisualSettings(visual.payload);
      dialog.showModal();
      var first = one(".hpw-wishlist-picker-choice", dialog);
      if (first) first.focus();
    });
  }

  function saveWithDestination(button, payload) {
    if (button.disabled) return;
    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = label("loadingText", "Loading wishlists…");
    request("/lists").then(function (data) {
      var lists = Array.isArray(data.lists) ? data.lists.filter(function (list) {
        return list && list.id != null && String(list.id) && list.name != null;
      }) : [];
      if (!lists.length) throw new Error("No wishlist is available. Please try again.");
      if (lists.length === 1) return lists[0];
      button.textContent = originalText;
      return chooseList(lists);
    }).then(function (destination) {
      if (!destination) {
        button.disabled = false;
        button.textContent = originalText;
        return false;
      }
      button.textContent = originalText;
      return save(button, payload, destination);
    }).catch(function (error) {
      button.disabled = false;
      button.textContent = originalText;
      toast(error.message || "Could not load your wishlists");
      return false;
    });
  }
  function loadSocialProof(wrapper, identity) {
    if (!visual.payload || visual.sections.socialProof === false || wrapper.dataset.hpwWishlistProofLoaded) return;
    wrapper.dataset.hpwWishlistProofLoaded = "true";
    var params = identity.productId ? "productId=" + encodeURIComponent(identity.productId) :
      identity.variantId ? "variantId=" + encodeURIComponent(identity.variantId) : "handle=" + encodeURIComponent(identity.handle);
    request("/social-proof?" + params, {}, true).then(function (result) {
      var count = Number(result.count || result.savedCount || 0);
      if (count > 0) one(".hpw-wishlist-proof", wrapper).textContent =
        count + " collector" + (count === 1 ? " has" : "s have") + " this saved";
    }).catch(function () {});
  }
  function injectProduct() {
    var form = one('form[action*="/cart/add"]');
    var existing = form && one("[data-hpw-wishlist-product]", form.parentElement);
    if (!form) return;
    if (existing) {
      loadSocialProof(existing, productIdentity(form));
      return;
    }
    var identity = productIdentity(form);
    if (!identity.productId && !identity.handle) return;
    var wrapper = document.createElement("div");
     wrapper.dataset.hpwWishlistProduct = "true";
     wrapper.dataset.hpwWishlistRoot = "true";
    wrapper.innerHTML = '<button type="button" class="hpw-wishlist-button" aria-pressed="false">♡ Add to Wishlist</button><span class="hpw-wishlist-proof" aria-live="polite"></span>';
    var button = one("button", wrapper);
    button.addEventListener("click", function () {
      saveWithDestination(button, productIdentity(form));
    });
    form.insertAdjacentElement("afterend", wrapper);
    loadSocialProof(wrapper, identity);
  }
  function injectCollection(root) {
    root = root || document;
    all('a[href*="/products/"]', root).forEach(function (link) {
      var match = link.href.match(/\/products\/([^/?#]+)/);
      var card = link.closest("product-card");
      if (!card) card = link.closest("li, article, .card, .grid__item");
      if (!match || !card || one("[data-hpw-wishlist-card]", card) ||
          card.closest('form[action*="/cart"], .cart-items, [data-cart-items]')) return;
      card.classList.add("hpw-wishlist-card-host");
      var image = one("img", card);
      var button = document.createElement("button");
      button.type = "button";
      button.className = "hpw-wishlist-heart";
       button.dataset.hpwWishlistCard = "true";
       button.dataset.hpwWishlistRoot = "true";
      button.setAttribute("aria-label", "Add " + (link.textContent.trim() || "product") + " to Wishlist");
      button.setAttribute("aria-pressed", "false");
      button.textContent = "♡";
      button.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        saveWithDestination(button, {
          handle: decodeURIComponent(match[1]),
          imageUrl: mediaUrl(image && (image.currentSrc || image.src)),
        });
      });
      card.appendChild(button);
    });
  }
  function cartContainerFor(item, index) {
    return one('[data-cart-item-key="' + css(item.key) + '"]') ||
      one('[data-key="' + css(item.key) + '"]') ||
      one('[data-variant-id="' + css(item.variant_id) + '"]') ||
      one('[data-line="' + (index + 1) + '"]') ||
      one('[data-index="' + (index + 1) + '"]') || null;
  }
  function injectCart() {
    if (!one('form[action*="/cart"], .cart-items, [data-cart-items]')) return;
    fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (response) { if (!response.ok) throw new Error("Could not read cart"); return response.json(); })
      .then(function (cart) {
        (cart.items || []).forEach(function (item, index) {
          var container = cartContainerFor(item, index);
          if (!container || one('[data-hpw-wishlist-cart="' + css(item.key) + '"]', container)) return;
          var button = document.createElement("button");
          button.type = "button";
          button.className = "hpw-wishlist-button hpw-wishlist-cart-save";
          button.dataset.hpwWishlistCart = item.key;
           button.dataset.hpwWishlistRoot = "true";
          button.textContent = label("saveForLater", "Save for Later");
          button.addEventListener("click", function () {
            save(button, {
              productId: id(item.product_id),
              variantId: id(item.variant_id),
              handle: item.handle || "",
              imageUrl: mediaUrl(item.image),
              savedForLater: true,
            }).then(function (saved) {
              if (!saved) return;
              if (window.confirm("Saved for Later. Remove this item from your cart?")) {
                return fetch("/cart/change.js", {
                  method: "POST",
                  credentials: "same-origin",
                  headers: { "Content-Type": "application/json", Accept: "application/json" },
                  body: JSON.stringify({ id: item.key, quantity: 0 }),
                }).then(function (response) {
                  if (!response.ok) throw new Error("Saved, but could not remove the cart item");
                  window.location.reload();
                });
              }
            }).catch(function (error) { toast(error.message); });
          });
          container.appendChild(button);
           if (visual.payload) applyVisualSettings(visual.payload);
        });
      }).catch(function () { /* Cart controls remain absent rather than guessing identity. */ });
  }
  function itemMarkup(item, readOnly) {
    var inStock = item.inStock !== false && item.stock !== "sold_out" && item.stock !== "out_of_stock";
    var delta = Number(item.priceDelta || 0);
    var productUrl = sameOriginUrl(item.handle ? "/products/" + encodeURIComponent(item.handle) : "", "#");
    var imageUrl = mediaUrl(item.imageUrl);
    var alternatives = (item.alternatives || []).map(function (alternative) {
      var url = sameOriginUrl(alternative.url || (alternative.handle && "/products/" + encodeURIComponent(alternative.handle)), "");
      return url ? '<a href="' + text(url) + '">' + text(alternative.title || "Alternative") + "</a>" : "";
    }).filter(Boolean);
    return '<article class="hpw-wishlist-item" data-item="' + text(item.id) + '">' +
      (visual.sections.showProductImages === false ? "" : imageUrl ? '<a href="' + text(productUrl) + '"><img src="' + text(imageUrl) + '" alt="' + text(item.title + " product image") + '"></a>' :
        '<div class="hpw-wishlist-image-placeholder" aria-hidden="true"></div>') + '<div>' +
      '<h3><a href="' + text(productUrl) + '">' + text(item.title) + "</a></h3>" +
      "<p><b>" + cad(item.currentPrice, item.currentPriceCents) + "</b>" +
      (delta ? ' <span class="' + (delta < 0 ? "hpw-wishlist-delta-down" : "hpw-wishlist-delta-up") + '">' +
        text((delta > 0 ? "+" : "") + cad(delta)) + " since saved</span>" : "") + "</p>" +
      "<p>" + text(inStock ? ("In stock: " + Number(item.stock || 0)) : "Sold out") + "</p>" +
      (item.restockOrdered ? '<span class="hpw-wishlist-badge">Restock ordered' + (item.etaMonth ? " · " + text(item.etaMonth) : "") + "</span>" : "") +
      (alternatives.length ? '<div class="hpw-wishlist-alternatives">Also available: ' + alternatives.join(", ") + "</div>" : "") +
      (sameOriginUrl(item.gradingUrl, "") ? '<div class="hpw-wishlist-alternatives"><a href="' + text(sameOriginUrl(item.gradingUrl)) + '">Submit this raw single for grading →</a></div>' : "") +
      '<div class="hpw-wishlist-actions">' +
       (item.variantId ? '<button class="hpw-wishlist-button" data-cart="' + text(id(item.variantId)) + '"' + (inStock ? "" : " disabled") + ">" + text(label("addToCart", "Add to Cart")) + "</button>" : "") +
      (!readOnly ? '<button class="hpw-wishlist-button" data-later="' + text(item.id) + '" data-later-value="' + (!item.savedForLater) + '">' + (item.savedForLater ? "Move to Wishlist" : "Save for Later") + "</button>" +
       '<button class="hpw-wishlist-button" data-remove="' + text(item.id) + '">Remove</button>' +
        '<label class="hpw-wishlist-target">Alert under $<input type="number" min="0.01" step="0.01" data-target-input="' + text(item.id) + '" value="' +
        text(item.targetPriceCents ? (Number(item.targetPriceCents) / 100).toFixed(2) : "") + '" placeholder="Optional"><button class="hpw-wishlist-button" data-target-save="' +
        text(item.id) + '">Save target</button></label>' : "") +
      "</div></div></article>";
  }
  function alertMarkup(alerts) {
    alerts = alerts || {};
    function checked(key) { return alerts[key] ? " checked" : ""; }
    return '<section class="hpw-wishlist-alerts"><h2>' + text(label("alertText", "Email alerts")) + '</h2>' +
      '<label><input type="checkbox" data-alert="enabled"' + checked("enabled") + '> Email me about my wishlist</label>' +
      '<div class="hpw-wishlist-alert-grid">' +
      '<label><input type="checkbox" data-alert="restockEnabled"' + checked("restockEnabled") + '> Restocks</label>' +
      '<label><input type="checkbox" data-alert="targetPriceEnabled"' + checked("targetPriceEnabled") + '> Target prices</label>' +
      '<label><input type="checkbox" data-alert="priceDropEnabled"' + checked("priceDropEnabled") + '> Price drops</label>' +
      '<label><input type="checkbox" data-alert="saleEnabled"' + checked("saleEnabled") + '> Sales</label>' +
      '<label><input type="checkbox" data-alert="lowStockEnabled"' + checked("lowStockEnabled") + '> Almost gone</label>' +
      '<label><input type="checkbox" data-alert="savedForLaterEnabled"' + checked("savedForLaterEnabled") + '> Saved-for-Later reminders</label>' +
      '<label><input type="checkbox" data-alert="staleWishEnabled"' + checked("staleWishEnabled") + '> Old-wish check-ins</label>' +
      '<label><input type="checkbox" data-alert="shipTogetherEnabled"' + checked("shipTogetherEnabled") + '> Free-shipping add-ons for open orders</label></div>' +
      '<button class="hpw-wishlist-button" data-alert-save>' + text(label("alertsSave", "Save email choices")) + '</button> ' +
      '<button class="hpw-wishlist-button" data-alert-unsubscribe>' + text(label("alertsUnsubscribe", "Turn off all emails")) + '</button>' +
      '<p><small>Restock and target-price emails are item alerts you request. Other reminders are sent only when your Shopify marketing email consent also allows them.</small></p></section>';
  }
  function renderPage(root, lists, selectedId, readOnly, alerts) {
    var selected = lists.filter(function (list) { return String(list.id) === String(selectedId); })[0] || lists[0];
    if (!selected) {
      root.innerHTML = '<h1>' + text(label("title", "Wishlist")) + '</h1><div class="hpw-wishlist-empty">' + text(label("emptyState", "This wishlist is empty.")) + '</div>' +
        (readOnly ? "" : '<button class="hpw-wishlist-button" data-new-list>Create a list</button>');
      bindPage(root, lists, null, readOnly, alerts);
      return;
    }
    var delta = Number(selected.delta != null ? selected.delta : selected.portfolioDelta || 0);
    var total = selected.currentTotalCents != null ? cad(0, selected.currentTotalCents) : cad(selected.currentTotal || selected.total || 0);
    root.innerHTML =
      '<div class="hpw-wishlist-toolbar"><div><h1>' + text(readOnly ? selected.name : label("title", "My Wishlist")) + '</h1><p>' +
      text(readOnly ? "A shared HPW collector list." : label("statusText", "Keep an eye on the cards you love.")) + "</p></div>" +
      (!readOnly ? '<button class="hpw-wishlist-button" data-new-list>' + text(label("newList", "New list")) + '</button>' : "") + "</div>" +
      (readOnly ? "" : alertMarkup(alerts)) +
      (readOnly ? "" : '<div class="hpw-wishlist-list-tabs" role="tablist" aria-label="Wishlist lists">' +
        lists.map(function (list) { return '<button class="hpw-wishlist-tab" role="tab" data-list="' + text(list.id) + '" aria-selected="' + (list === selected) + '">' + text(list.name) + "</button>"; }).join("") + "</div>") +
       '<section class="hpw-wishlist-summary" aria-label="Wishlist controls">' +
      (visual.sections.portfolioSummary === false ? "" : '<div><small>Current value</small><b>' + total +
      '</b></div><div><small>Since saved</small><b class="' + (delta < 0 ? "hpw-wishlist-delta-down" : "hpw-wishlist-delta-up") + '">' +
      text((delta >= 0 ? "+" : "") + cad(delta)) + "</b></div>") +
      (!readOnly ? '<button class="hpw-wishlist-button" data-rename="' + text(selected.id) + '">Rename</button><button class="hpw-wishlist-button" data-delete-list="' + text(selected.id) + '">Delete list</button>' +
         (visual.sections.showShareControls === false ? "" : '<button class="hpw-wishlist-button" data-share="' + text(selected.id) + '">' + text(label("shareTitle", "Copy share link")) + '</button>') : "") +
        '</section><div class="hpw-wishlist-items">' + ((selected.items || []).map(function (item) { return itemMarkup(item, readOnly); }).join("") || '<div class="hpw-wishlist-empty">' + text(label("emptyState", "No saved cards in this list yet.")) + '</div>') + "</div>";
    bindPage(root, lists, selected, readOnly, alerts);
  }
  function copy(value) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(value);
    var area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    var copied = document.execCommand("copy");
    area.remove();
    return copied ? Promise.resolve() : Promise.reject(new Error("Copy failed"));
  }
  function loadPrivate(root, selectedId) {
    root.innerHTML = '<p class="hpw-wishlist-loading" role="status">' + text(label("loadingText", "Loading your wishlist…")) + "</p>";
    request("/state").then(function (data) { renderPage(root, data.lists || [], selectedId, false, data.alerts || {}); })
      .catch(function (error) { root.innerHTML = '<div class="hpw-wishlist-error" role="alert">' + text(error.message) + "</div>"; });
  }
  function bindPage(root, lists, selected, readOnly, alerts) {
    all("[data-list]", root).forEach(function (button) { button.addEventListener("click", function () { renderPage(root, lists, button.dataset.list, false, alerts); }); });
    all("[data-new-list]", root).forEach(function (button) { button.addEventListener("click", function () {
      var name = window.prompt("Name your list");
      if (name && name.trim()) request("/lists", { method: "POST", body: JSON.stringify({ name: name.trim() }) }).then(function () { loadPrivate(root); }).catch(function (error) { toast(error.message); });
    }); });
    if (!readOnly && selected) {
      var rename = one("[data-rename]", root);
      if (rename) rename.addEventListener("click", function () {
        var name = window.prompt("Rename this list", selected.name);
        if (name && name.trim()) request("/lists/" + encodeURIComponent(selected.id), { method: "PATCH", body: JSON.stringify({ name: name.trim() }) }).then(function () { loadPrivate(root, selected.id); }).catch(function (error) { toast(error.message); });
      });
      var removeList = one("[data-delete-list]", root);
      if (removeList) removeList.addEventListener("click", function () {
        if (window.confirm("Delete this list and all its saved items?")) request("/lists/" + encodeURIComponent(selected.id), { method: "DELETE" }).then(function () { loadPrivate(root); }).catch(function (error) { toast(error.message); });
      });
      var share = one("[data-share]", root);
      if (share) share.addEventListener("click", function () {
        request("/lists/" + encodeURIComponent(selected.id) + "/share", { method: "POST" }).then(function (result) {
          var url = trustedShareUrl(result.url);
          if (!url) throw new Error("The share link returned by the server was invalid");
          return copy(url);
        }).then(function () { toast(label("shareCopied", "Read-only share link copied")); }).catch(function (error) { toast(error.message); });
      });
    }
    all("[data-cart]", root).forEach(function (button) { button.addEventListener("click", function () {
      button.disabled = true;
      fetch("/cart/add.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: [{ id: button.dataset.cart, quantity: 1 }] }) })
        .then(function (response) { if (!response.ok) throw new Error("Could not add this card to your cart"); window.location.assign("/cart"); })
        .catch(function (error) { button.disabled = false; toast(error.message); });
    }); });
    if (readOnly) return;
    var alertSave = one("[data-alert-save]", root);
    if (alertSave) alertSave.addEventListener("click", function () {
      var payload = {};
      all("[data-alert]", root).forEach(function (input) { payload[input.dataset.alert] = input.checked; });
      request("/alerts", { method: "PATCH", body: JSON.stringify(payload) })
        .then(function () { toast("Wishlist email choices saved"); loadPrivate(root, selected && selected.id); })
        .catch(function (error) { toast(error.message); });
    });
    var unsubscribe = one("[data-alert-unsubscribe]", root);
    if (unsubscribe) unsubscribe.addEventListener("click", function () {
      request("/alerts/unsubscribe", { method: "POST" })
        .then(function () { toast("Wishlist emails turned off"); loadPrivate(root, selected && selected.id); })
        .catch(function (error) { toast(error.message); });
    });
    all("[data-remove]", root).forEach(function (button) { button.addEventListener("click", function () {
      request("/items/" + encodeURIComponent(button.dataset.remove), { method: "DELETE" }).then(function () { loadPrivate(root, selected.id); }).catch(function (error) { toast(error.message); });
    }); });
    all("[data-later]", root).forEach(function (button) { button.addEventListener("click", function () {
      request("/items/" + encodeURIComponent(button.dataset.later), { method: "PATCH", body: JSON.stringify({ savedForLater: button.dataset.laterValue === "true" }) })
        .then(function () { loadPrivate(root, selected.id); }).catch(function (error) { toast(error.message); });
    }); });
    all("[data-target-save]", root).forEach(function (button) { button.addEventListener("click", function () {
      var input = one('[data-target-input="' + button.dataset.targetSave + '"]', root);
      var dollars = input && input.value.trim() ? Number(input.value) : null;
      if (dollars !== null && (!isFinite(dollars) || dollars <= 0)) { toast("Enter a valid target price"); return; }
      request("/items/" + encodeURIComponent(button.dataset.targetSave) + "/alerts", {
        method: "PATCH",
        body: JSON.stringify({ targetPriceCents: dollars === null ? null : Math.round(dollars * 100) })
      }).then(function () { toast(dollars === null ? "Target price cleared" : "Target price saved"); })
        .catch(function (error) { toast(error.message); });
    }); });
  }
  function wishlistPage() {
    var root = one("[data-hpw-wishlist-page]");
    if (!root) return;
    root.dataset.hpwWishlistRoot = "true";
    var share = new URLSearchParams(window.location.search).get("share");
    if (share) {
      request("/share/" + encodeURIComponent(share), {}, true)
        .then(function (data) { renderPage(root, data.list ? [data.list] : [], data.list && data.list.id, true); })
        .catch(function (error) { root.innerHTML = '<div class="hpw-wishlist-error" role="alert">' + text(error.message) + "</div>"; });
    } else loadPrivate(root);
  }
  function start() {
    // Appearance is progressive enhancement. A public-settings outage must
    // never delay the native controls or account-backed Wishlist page.
    injectProduct();
    injectCollection();
    injectCart();
    wishlistPage();
    loadVisualSettings().then(function () { applyVisualSettings(visual.payload); injectProduct(); });
    // This is a public-only request; it never probes account/auth endpoints.
    // A bounded cadence makes a populated -> empty publication clear in place.
    window.setInterval(function () { loadVisualSettings().then(function () {
      applyVisualSettings(visual.payload);
    }); }, VISUAL_REVALIDATE_MS);
    var modeChanged = function () { if (visual.payload) applyVisualSettings(visual.payload); };
    var media = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
    if (media && media.addEventListener) media.addEventListener("change", modeChanged);
    new MutationObserver(modeChanged).observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    new MutationObserver(modeChanged).observe(document.body, { attributes: true, attributeFilter: ["class"] });
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href*="/products/"]')) {
            injectCollection(node.parentElement || node);
          } else {
            injectCollection(node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
  document.addEventListener("shopify:section:load", function () {
     loadVisualSettings().then(function () { injectProduct(); injectCollection(); applyVisualSettings(visual.payload); });
  });
}());
